import { useMemo } from 'react';
import type { MortgageYear } from '../engine/mortgage';
import type { ProjectionYear } from '../engine/projection';
import type { LoanKind } from '../types';
import { Card, Chip, SectionHeader } from '../ui';
import { fmtCompact, fmtMoney } from '../utils/format';

interface Props {
  years: ProjectionYear[];
  mortgage: MortgageYear[];
  /** Leftmost year shown on the main net-worth chart. The strip spans the
   *  same range so bars align visually with chart events. */
  startingCalendarYear: number;
  /** Total years rendered; bars are laid out as a fraction of this. */
  totalYears: number;
  /** Invoked when the user clicks a loan bar. Receives the source event ID
   *  (the event that created the loan). The host page navigates / expands
   *  the event editor. */
  onOpenEvent?: (eventId: string) => void;
}

interface LoanBand {
  id: string;
  label: string;
  /** 'mortgage' for the primary residence; union with non-mortgage kinds. */
  kind: 'mortgage' | LoanKind;
  startYear: number;
  endYear: number;
  originalPrincipal: number;
  /** Peak balance during the active window (for sorting and labels). */
  peakBalance: number;
  /** Monthly P&I (0 if we don't know — mortgage primary). */
  monthlyPI: number;
  /** Annual interest rate, 0 if unknown. */
  annualRate: number;
  /** Event that created this loan (undefined for the primary mortgage
   *  unless created by a home_buy event this session — for seed mortgages,
   *  the user edits them via Assumptions). */
  sourceEventId?: string;
  sourceEventName?: string;
}

const KIND_COLOR: Record<LoanBand['kind'], string> = {
  mortgage: '#4f6bff',
  auto: '#f59e0b',
  personal: '#64748b',
  heloc: '#8b5cf6',
  retirement_401k: '#dc2626',
};

const KIND_LABEL: Record<LoanBand['kind'], string> = {
  mortgage: 'Mortgage',
  auto: 'Auto',
  personal: 'Personal',
  heloc: 'HELOC',
  retirement_401k: '401(k) loan',
};

/**
 * A Gantt-style strip showing every active loan across the projection
 * horizon. Each bar starts the year the loan is taken out, extends through
 * its active period, and ends when it's paid off. Bar length = lifespan;
 * color = loan kind. Aligns with the main chart's year range for visual
 * correspondence with event markers above.
 */
export function LoansStrip({ years, mortgage, startingCalendarYear, totalYears, onOpenEvent }: Props) {
  const bands = useMemo<LoanBand[]>(() => {
    const all: LoanBand[] = [];
    // Non-mortgage loans: walk the years and accumulate one band per unique loan id.
    const byId = new Map<string, LoanBand>();
    for (const y of years) {
      for (const l of y.loans) {
        const existing = byId.get(l.id);
        if (!existing) {
          byId.set(l.id, {
            id: l.id,
            label: l.label,
            kind: l.loanKind,
            startYear: y.calendarYear,
            endYear: y.calendarYear,
            originalPrincipal: l.originalPrincipal,
            peakBalance: l.balance,
            monthlyPI: l.monthlyPI,
            annualRate: l.annualRate,
            sourceEventId: l.sourceEventId,
            sourceEventName: l.sourceEventName,
          });
        } else {
          existing.endYear = y.calendarYear;
          if (l.balance > existing.peakBalance) existing.peakBalance = l.balance;
        }
      }
    }
    all.push(...byId.values());

    // Primary mortgage: contiguous runs where balance is outstanding.
    let run: { start: number; end: number; principal: number } | null = null;
    const flush = () => {
      if (!run) return;
      all.push({
        id: `mortgage-${run.start}`,
        label: 'Home mortgage',
        kind: 'mortgage',
        startYear: run.start,
        endYear: run.end,
        originalPrincipal: run.principal,
        peakBalance: run.principal,
        monthlyPI: 0,
        annualRate: 0,
      });
      run = null;
    };
    for (const m of mortgage) {
      const active = m.begBalance > 0.5 || m.principalPaid > 0.5;
      if (active) {
        if (!run) run = { start: m.calendarYear, end: m.calendarYear, principal: m.begBalance };
        else run.end = m.calendarYear;
      } else if (run) {
        flush();
      }
    }
    flush();

    return all.sort((a, b) => a.startYear - b.startYear || b.peakBalance - a.peakBalance);
  }, [years, mortgage]);

  if (bands.length === 0) {
    return null;
  }

  const endYear = startingCalendarYear + totalYears - 1;
  const totalBalance = bands
    .filter(b => b.kind !== 'mortgage' && b.kind !== 'retirement_401k')
    .reduce((s, b) => s + b.peakBalance, 0);
  const hasMortgage = bands.some(b => b.kind === 'mortgage');

  return (
    <Card flush>
      <SectionHeader
        title={`Loans & liabilities (${bands.length})`}
        subtitle="Lifespan of every outstanding loan — mortgages, auto, HELOC, 401(k) loans"
        bordered
        muted
        right={
          <div className="flex gap-1.5">
            {hasMortgage && (
              <Chip tone="accent" size="sm" dotColor={KIND_COLOR.mortgage}>
                Mortgage
              </Chip>
            )}
            {totalBalance > 0 && (
              <Chip tone="neutral" size="sm">
                {fmtCompact(totalBalance)} other
              </Chip>
            )}
          </div>
        }
      />
      <div className="p-3">
        <YearAxis startYear={startingCalendarYear} endYear={endYear} />
        <ul className="mt-1 space-y-1.5">
          {bands.map(b => (
            <li key={b.id}>
              <LoanRow
                band={b}
                startYear={startingCalendarYear}
                endYear={endYear}
                onOpenEvent={onOpenEvent}
              />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

const LABEL_WIDTH_PCT = 34;

function YearAxis({ startYear, endYear }: { startYear: number; endYear: number }) {
  const span = endYear - startYear;
  // Pick a tick interval so we get 5-8 labels across the range.
  const step = span <= 10 ? 2 : span <= 20 ? 5 : span <= 40 ? 10 : 20;
  const ticks: number[] = [];
  for (let y = startYear; y <= endYear; y += step) ticks.push(y);
  return (
    <div className="relative mb-1 h-4 text-[10px] tabular-nums text-ink-500 dark:text-ink-300">
      <div
        className="absolute inset-0"
        style={{ left: `${LABEL_WIDTH_PCT}%`, right: '0' }}
      >
        {ticks.map(y => (
          <span
            key={y}
            className="absolute -translate-x-1/2"
            style={{ left: `${((y - startYear) / (span || 1)) * 100}%` }}
          >
            {y}
          </span>
        ))}
      </div>
    </div>
  );
}

function LoanRow({
  band,
  startYear,
  endYear,
  onOpenEvent,
}: {
  band: LoanBand;
  startYear: number;
  endYear: number;
  onOpenEvent?: (eventId: string) => void;
}) {
  const span = endYear - startYear || 1;
  const leftPct = Math.max(0, (band.startYear - startYear) / span) * 100;
  const widthPct =
    Math.max(0.5, (band.endYear - band.startYear + 1) / span) * 100;
  const color = KIND_COLOR[band.kind];
  const ratePct = band.annualRate > 0 ? `${(band.annualRate * 100).toFixed(2)}%` : null;
  const pi = band.monthlyPI > 0 ? `${fmtMoney(band.monthlyPI, { decimals: 0 })}/mo` : null;
  const clickable = !!onOpenEvent && !!band.sourceEventId;
  const click = () => {
    if (clickable) onOpenEvent!(band.sourceEventId!);
  };
  const title = `${band.label}${
    band.sourceEventName ? ` — from "${band.sourceEventName}"` : ''
  } · ${band.startYear}–${band.endYear}${
    ratePct ? ` · ${ratePct}` : ''
  }${pi ? ` · ${pi}` : ''}${clickable ? ' · click to edit the event' : ''}`;

  const content = (
    <>
      <div
        className="flex shrink-0 flex-col gap-0 overflow-hidden"
        style={{ width: `${LABEL_WIDTH_PCT}%` }}
      >
        <div className="flex items-baseline gap-1.5">
          <span
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <span className="truncate font-semibold text-ink-900 dark:text-ink-50">
            {band.label}
          </span>
          <span className="shrink-0 text-ink-500 dark:text-ink-300">
            {KIND_LABEL[band.kind]}
          </span>
        </div>
        {band.sourceEventName && (
          <span className="truncate pl-3.5 text-[10px] text-ink-500 dark:text-ink-300">
            from "{band.sourceEventName}"
          </span>
        )}
      </div>
      <div className="relative h-5 flex-1 rounded-md bg-ink-50 dark:bg-ink-800/60">
        <div
          className="absolute top-1/2 h-3 -translate-y-1/2 rounded"
          style={{
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            backgroundColor: color,
            opacity: 0.85,
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 text-[10px] font-mono font-semibold tabular-nums text-white"
          style={{
            left: `calc(${leftPct}% + 4px)`,
            maxWidth: `calc(${widthPct}% - 8px)`,
          }}
        >
          {band.originalPrincipal > 0
            ? `$${fmtCompact(band.originalPrincipal)}${ratePct ? ` @ ${ratePct}` : ''}`
            : ''}
        </div>
      </div>
      <div className="shrink-0 text-right font-mono tabular-nums text-ink-500 dark:text-ink-300" style={{ width: 62 }}>
        {band.endYear - band.startYear + 1}y
      </div>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={click}
        title={title}
        className="flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-[11px] transition hover:bg-ink-50 dark:hover:bg-ink-800/60"
      >
        {content}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 px-1 text-[11px]" title={title}>
      {content}
    </div>
  );
}
